import {
  loadVitalRepNoteByAppointment,
  normalizeVitalRepStructuredClinicalData,
  VITALREP_VITALS_SOURCE,
} from "@/pages/doctors/appointments/lib/vitalRepNote.js";
import supabase from "@/services/supabaseClient";

function clean(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function first(...values) {
  for (const value of values) {
    const next = clean(value);
    if (next) return next;
  }
  return "";
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return "";
  }
}

export async function loadLatestVitalRepPatientContext(patientUserId = "") {
  const cleanPatientUserId = clean(patientUserId);
  if (!cleanPatientUserId) return null;

  const { data: patientRow, error: patientError } = await supabase
    .from("patients")
    .select("id,user_id")
    .eq("user_id", cleanPatientUserId)
    .maybeSingle();
  if (patientError || !patientRow?.id) return null;

  const { data: appointmentRows, error: appointmentError } = await supabase
    .from("appointments")
    .select("id,appointment_date,status,consultation_type,booked_service_name,diagnosis_title,diagnosis_notes,notes")
    .eq("patient_id", patientRow.id)
    .order("appointment_date", { ascending: false, nullsFirst: false })
    .order("updated_at", { ascending: false, nullsFirst: false })
    .limit(8);
  if (appointmentError) return null;

  for (const appointment of appointmentRows || []) {
    const appointmentId = clean(appointment?.id);
    if (!appointmentId) continue;
    const vitalRep = await loadVitalRepNoteByAppointment(appointmentId);
    if (!vitalRep) continue;

    const { data: prescription } = await supabase
      .from("prescriptions")
      .select("id,status,meta,diagnosis,notes")
      .eq("appointment_id", appointmentId)
      .eq("patient_user_id", cleanPatientUserId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const structured = normalizeVitalRepStructuredClinicalData(vitalRep.structuredClinicalData || {});
    const prescriptionMeta = prescription?.meta && typeof prescription.meta === "object"
      ? prescription.meta
      : {};

    return {
      kind: "my_ehr_vitalrep_context",
      appointmentId,
      visitDate: formatDate(appointment?.appointment_date),
      service: first(appointment?.booked_service_name, appointment?.consultation_type),
      reasonForVisit: first(structured.reasonForVisit, vitalRep.sourceSnapshot?.serviceName),
      assessment: first(structured.assessment, appointment?.diagnosis_title, prescription?.diagnosis),
      treatmentNextSteps: first(structured.treatmentNextSteps, appointment?.notes, vitalRep.summary),
      followUpAdvice: first(structured.followUpAdvice),
      testsLabsRequested: first(structured.testsLabsRequested),
      vitalsSource: first(structured.vitals?.source, VITALREP_VITALS_SOURCE.NOT_CAPTURED),
      medicationSummary: first(
        structured.medicationSummary,
        prescriptionMeta.medicineName,
        prescriptionMeta.medicine,
        prescriptionMeta.name,
      ),
      prescriptionId: clean(prescription?.id),
      vitalRepStatus: clean(vitalRep.status),
    };
  }

  return null;
}
